import { sekmeGorunur } from './pazarlama-durum';
import { useCallback, useEffect, useState } from 'react';
import {
  api,
  ApiError,
  type AbandonedCartSegment,
  type CartRecoveryDispatchResult,
  type ConsentHistory,
  type ConsentLedgerPage,
  type ConsentText,
  type MarketingLogPage,
  type Me,
  type NewsletterCadenceStatus,
  type NewsletterReconciliationPage,
  type NewsletterSubscriberPage,
  type SegmentRecipients,
} from './api';
import { durumAdi, MUSTERI_TURU, ORTAK, para, tamSayi, tarihSaat, TANIMSIZ } from './metin/ortak';
import { WORKFLOW_METIN } from './metin/workflow';
import {
  ENGEL_SEBEBI,
  KANAL,
  KANIT_GUCU,
  PANEL_DILI,
  PAZARLAMA_METIN,
  RIZA_DAYANAGI,
  RIZA_DURUMU,
} from './metin/pazarlama';

/**
 * CRM-A24: Pazarlama bölümünün rıza ekranı.
 *
 * Altı bölüm ve hepsi aynı soruya farklı yerlerden bakıyor: *bu kişiye yazabilir miyiz, ve bunu
 * nereden biliyoruz.*
 *
 * Sekmeyi gizlemek yetkilendirme değil (`Admin.tsx`in aynı notu): arkadaki her uç sunucuda
 * yeniden kontrol ediliyor ve izinsiz istek 403 dönüyor. Buradaki gösterme/gizleme yalnız
 * kullanılamayacak bir düğmeyi göstermemek için.
 */

const KANALLAR = ['EMAIL', 'SMS', 'WHATSAPP'] as const;
const DURUMLAR = ['PENDING', 'GRANTED', 'WITHDRAWN'] as const;
const BULTEN_DURUMLARI = ['PENDING', 'CONFIRMED', 'UNSUBSCRIBED', 'SUPPRESSED'] as const;

function hataMetni(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) return caught.message;
  return fallback;
}

function karsilik(tablo: Readonly<Record<string, string>>, kod: string | null | undefined): string {
  if (kod === null || kod === undefined) return TANIMSIZ;
  return tablo[kod] ?? kod;
}

/**
 * Kanal bazlı onay formunun önizlemesi.
 *
 * **Üç kutu da boş başlıyor ve hiçbir kod yolu onları açmıyor.** Önceden işaretli bir kutu,
 * kişinin vermediği bir izni vermiş gibi göstermenin en kısa yolu; Ticari İletişim Yönetmeliği'nin
 * önlemek için var olduğu kusur tam olarak bu. Kutular ayrı ayrı: e-postaya "evet" demek SMS'e
 * "evet" demek değildir.
 *
 * Buradan kayıt **yapılmıyor**. Rızayı kişi kendi veriyor — web formundan ya da üye panelinden —
 * ve e-postada onay bağlantısı tıklanmadan izin tamamlanmıyor. Bu ekran metnin kanal başına ayrı
 * olduğunu ve varsayılanın kapalı olduğunu gösteriyor.
 */
function OnayFormuOnizleme({ metinler }: { metinler: ConsentText[] }) {
  const [secili, setSecili] = useState<Record<string, boolean>>({});
  const metin = PAZARLAMA_METIN.form;

  return (
    <section className="content-section" data-testid="consent-form-preview" aria-labelledby="consent-form-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{PAZARLAMA_METIN.riza.ustBaslik}</span>
          <h2 id="consent-form-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
      </div>

      <div className="callout" role="note">{metin.uyari}</div>

      <div className="stack">
        {KANALLAR.map((kanal) => {
          // Panelin dilindeki sürüm. Dil süzgeci olmadan `find` sıradaki ilk yürürlükteki satırı
          // alıyordu ve Türkçe ekranda İngilizce cümle görünüyordu (ölçüldü, 24.08.2026).
          const yururlukte =
            metinler.find(
              (satir) => satir.channel === kanal && satir.inForce === true && satir.locale === PANEL_DILI,
            ) ?? metinler.find((satir) => satir.channel === kanal && satir.inForce === true);
          if (yururlukte === undefined) {
            return (
              <p key={kanal} className="muted" data-testid={`consent-box-missing-${kanal}`}>
                <strong>{KANAL[kanal]}:</strong> {metin.kutuYok}
              </p>
            );
          }
          return (
            <label key={kanal} className="consent-box" data-testid={`consent-box-${kanal}`}>
              <input
                type="checkbox"
                name={`consent-${kanal}`}
                data-testid={`consent-checkbox-${kanal}`}
                checked={secili[kanal] ?? false}
                onChange={(olay) => setSecili((onceki) => ({ ...onceki, [kanal]: olay.target.checked }))}
              />
              {/* Aradaki boşluk metni kaldırıldı: ızgarada üçüncü bir hücre olurdu. */}
              <span>
                <span data-veri>{yururlukte.summary}</span>{' '}
                <small className="muted">
                  ({KANAL[kanal]} · {yururlukte.version})
                </small>
              </span>
            </label>
          );
        })}
      </div>
    </section>
  );
}

/** Yayımlanmış metin sürümleri, ve yenisini yayımlayan form. */
function RizaMetinleri({ yazabilir, onDegisti }: { yazabilir: boolean; onDegisti: (metinler: ConsentText[]) => void }) {
  const [metinler, setMetinler] = useState<ConsentText[]>([]);
  const [hata, setHata] = useState<string | null>(null);
  const [tamam, setTamam] = useState<string | null>(null);
  const [taslak, setTaslak] = useState({ channel: 'EMAIL', version: '', locale: 'tr', summary: '', body: '' });
  const metin = PAZARLAMA_METIN.metinler;

  const yukle = useCallback(async () => {
    setHata(null);
    try {
      const cevap = await api.consentTexts();
      setMetinler(cevap.items);
      onDegisti(cevap.items);
    } catch (caught) {
      setHata(hataMetni(caught, metin.hata));
    }
  }, [metin.hata, onDegisti]);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  const yayimla = async () => {
    setHata(null);
    setTamam(null);
    try {
      await api.publishConsentText(taslak);
      setTaslak({ ...taslak, version: '', summary: '', body: '' });
      setTamam(metin.tamam);
      await yukle();
    } catch (caught) {
      setHata(hataMetni(caught, metin.hata));
    }
  };

  return (
    <section className="content-section" data-testid="consent-texts" aria-labelledby="consent-texts-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{PAZARLAMA_METIN.riza.ustBaslik}</span>
          <h2 id="consent-texts-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
        <span className="count-badge">{tamSayi(metinler.length)} {ORTAK.kayit}</span>
      </div>

      {hata ? <div className="error callout" role="alert">{hata}</div> : null}
      {tamam ? <div className="callout" role="status">{tamam}</div> : null}

      <div className="table-wrap">
        <table data-testid="consent-text-table">
          <thead>
            <tr>
              <th>{metin.sutunKanal}</th>
              <th>{metin.sutunSurum}</th>
              <th>{metin.sutunDil}</th>
              <th>{metin.sutunOzet}</th>
              <th>{metin.sutunGecerlilik}</th>
            </tr>
          </thead>
          <tbody>
            {metinler.length === 0 ? (
              <tr><td colSpan={5} className="muted">{metin.bos}</td></tr>
            ) : (
              metinler.map((satir) => (
                <tr key={satir.id}>
                  <td>{karsilik(KANAL, satir.channel)}</td>
                  <td><code>{satir.version}</code></td>
                  <td>{satir.locale}</td>
                  {/* Müşteriye gösterilen metnin kendisi: ekranın alıntısı, ekranın cümlesi değil. */}
                  <td data-veri>{satir.summary}</td>
                  <td>
                    {satir.inForce === true
                      ? metin.yururlukte
                      : new Date(satir.effectiveFrom) > new Date()
                        ? metin.ileri
                        : metin.eski}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {yazabilir ? (
        <div className="toolbar" data-testid="consent-text-publish">
          <label>
            {metin.sutunKanal}{' '}
            <select value={taslak.channel} onChange={(olay) => setTaslak({ ...taslak, channel: olay.target.value })}>
              {KANALLAR.map((kanal) => <option key={kanal} value={kanal}>{KANAL[kanal]}</option>)}
            </select>
          </label>
          <label>
            {metin.surumAlani}{' '}
            <input value={taslak.version} onChange={(olay) => setTaslak({ ...taslak, version: olay.target.value })} />
          </label>
          <label>
            {metin.ozetAlani}{' '}
            <input value={taslak.summary} onChange={(olay) => setTaslak({ ...taslak, summary: olay.target.value })} />
          </label>
          <label>
            {metin.govdeAlani}{' '}
            <textarea value={taslak.body} onChange={(olay) => setTaslak({ ...taslak, body: olay.target.value })} />
          </label>
          <button type="button" onClick={() => void yayimla()}>{metin.yayimla}</button>
        </div>
      ) : null}
    </section>
  );
}

/** Bir kişinin bugünkü cevabı ve tam geçmişi. */
function KisiGecmisi({ yazabilir }: { yazabilir: boolean }) {
  const [sorgu, setSorgu] = useState('');
  const [not, setNot] = useState('');
  const [sonuc, setSonuc] = useState<ConsentHistory | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [tamam, setTamam] = useState<string | null>(null);
  const metin = PAZARLAMA_METIN.sorgu;

  const getir = async () => {
    setHata(null);
    setTamam(null);
    try {
      setSonuc(await api.consentHistory({ subject: sorgu }));
    } catch (caught) {
      setSonuc(null);
      setHata(hataMetni(caught, PAZARLAMA_METIN.riza.hata));
    }
  };

  const cikar = async (kanal: string) => {
    setHata(null);
    try {
      await api.withdrawConsent({ subject: sorgu, channel: kanal, ...(not === '' ? {} : { note: not }) });
      setTamam(PAZARLAMA_METIN.cikar.tamam);
      await getir();
    } catch (caught) {
      setHata(hataMetni(caught, PAZARLAMA_METIN.cikar.hata));
    }
  };

  return (
    <section className="content-section" data-testid="consent-history" aria-labelledby="consent-history-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{PAZARLAMA_METIN.riza.ustBaslik}</span>
          <h2 id="consent-history-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
      </div>

      {hata ? <div className="error callout" role="alert">{hata}</div> : null}
      {tamam ? <div className="callout" role="status">{tamam}</div> : null}

      <div className="toolbar">
        <label>
          {metin.alan}{' '}
          <input
            data-testid="consent-subject"
            value={sorgu}
            onChange={(olay) => setSorgu(olay.target.value)}
            autoComplete="off"
          />
        </label>
        <button type="button" data-testid="consent-history-load" onClick={() => void getir()}>{metin.dugme}</button>
        {yazabilir ? (
          <label>
            {PAZARLAMA_METIN.cikar.sebep}{' '}
            <input value={not} onChange={(olay) => setNot(olay.target.value)} />
          </label>
        ) : null}
      </div>

      {sonuc === null ? null : (
        <>
          <h3>{metin.guncelBaslik}</h3>
          <div className="table-wrap">
            <table data-testid="consent-current">
              <thead>
                <tr>
                  <th>{PAZARLAMA_METIN.riza.sutunKanal}</th>
                  <th>{PAZARLAMA_METIN.riza.sutunDurum}</th>
                  <th>{metin.dayanak}</th>
                  <th>{metin.kanit}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sonuc.current.map((satir) => (
                  <tr key={satir.channel} data-testid={`consent-current-${satir.channel}`}>
                    <td>{karsilik(KANAL, satir.channel)}</td>
                    <td>
                      {satir.sendable === true
                        ? metin.yazilabilir
                        : `${metin.yazilamaz} — ${karsilik(ENGEL_SEBEBI, satir.blockedBy)}`}
                    </td>
                    <td>{karsilik(RIZA_DAYANAGI, satir.basis)}</td>
                    <td>{karsilik(KANIT_GUCU, satir.evidence)}</td>
                    <td>
                      {yazabilir && satir.sendable === true ? (
                        <button type="button" onClick={() => void cikar(satir.channel)}>
                          {PAZARLAMA_METIN.cikar.dugme}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {sonuc.items.length === 0 ? (
            <p className="muted">{metin.bosSonuc}</p>
          ) : (
            <div className="table-wrap">
              <table data-testid="consent-history-table">
                <thead>
                  <tr>
                    <th>{PAZARLAMA_METIN.riza.sutunAn}</th>
                    <th>{PAZARLAMA_METIN.riza.sutunKanal}</th>
                    <th>{PAZARLAMA_METIN.riza.sutunDurum}</th>
                    <th>{PAZARLAMA_METIN.riza.sutunKaynak}</th>
                    <th>{PAZARLAMA_METIN.riza.sutunMetin}</th>
                  </tr>
                </thead>
                <tbody>
                  {sonuc.items.map((satir) => (
                    <tr key={satir.id}>
                      <td>{tarihSaat(satir.occurredAt)}</td>
                      <td>{karsilik(KANAL, satir.channel)}</td>
                      <td>{karsilik(RIZA_DURUMU, satir.status)}</td>
                      {/*
                        Rızanın nereden geldiği: `web-form`, `email-one-click`, `/tur/dubai-4-gece`.
                        Uçtan gelen ham kaynak anahtarı ve sayfa yolu — kanıtın parçası, bu yüzden
                        çevrilmiyor ve kısaltılmıyor. `code` gibi işaretleniyor.
                      */}
                      <td data-veri>{satir.source}{satir.sourcePage === null ? '' : ` · ${satir.sourcePage}`}</td>
                      <td><code>{satir.text?.version ?? satir.textVersionId}</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** Bir kitlenin kaçına gerçekten yazılabileceği. */
function GonderimKapisi() {
  const [kanal, setKanal] = useState<string>('EMAIL');
  const [kampanya, setKampanya] = useState('');
  const [sonuc, setSonuc] = useState<SegmentRecipients | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const metin = PAZARLAMA_METIN.kapi;

  const olc = async () => {
    setHata(null);
    try {
      setSonuc(await api.segmentRecipients({ channel: kanal, ...(kampanya === '' ? {} : { campaignRef: kampanya }) }));
    } catch (caught) {
      setSonuc(null);
      setHata(hataMetni(caught, metin.hata));
    }
  };

  return (
    <section className="content-section" data-testid="consent-gate" aria-labelledby="consent-gate-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{PAZARLAMA_METIN.riza.ustBaslik}</span>
          <h2 id="consent-gate-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
      </div>

      {hata ? <div className="error callout" role="alert">{hata}</div> : null}

      <div className="toolbar">
        <label>
          {metin.kanal}{' '}
          <select value={kanal} onChange={(olay) => setKanal(olay.target.value)}>
            {KANALLAR.map((deger) => <option key={deger} value={deger}>{KANAL[deger]}</option>)}
          </select>
        </label>
        <label>
          {metin.kampanya}{' '}
          <input value={kampanya} onChange={(olay) => setKampanya(olay.target.value)} />
        </label>
        <button type="button" data-testid="consent-gate-run" onClick={() => void olc()}>{metin.dugme}</button>
      </div>

      {sonuc === null ? null : (
        <>
          <div className="stat-row" data-testid="consent-gate-result">
            <span>{metin.aday}: <strong>{tamSayi(sonuc.candidates)}</strong></span>
            <span>{metin.izinli}: <strong>{tamSayi(sonuc.allowed)}</strong></span>
            <span>{metin.engelli}: <strong>{tamSayi(sonuc.blocked)}</strong></span>
          </div>
          {sonuc.truncated ? <div className="callout" role="status">{metin.kesildi}</div> : null}
          <div className="table-wrap">
            <table>
              <tbody>
                {Object.entries(sonuc.blockedBy).map(([sebep, adet]) => (
                  <tr key={sebep}>
                    <td>{karsilik(ENGEL_SEBEBI, sebep)}</td>
                    <td>{tamSayi(adet)}</td>
                  </tr>
                ))}
                {Object.entries(sonuc.evidence).map(([kanit, adet]) => (
                  <tr key={kanit}>
                    <td>{karsilik(KANIT_GUCU, kanit)}</td>
                    <td>{tamSayi(adet)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

/**
 * Satış akışı vakası madde 14/15: "sepeti olan müşteri" segmenti.
 *
 * `GonderimKapisi`yle aynı gizlilik disiplini: `cartSegment` yalnız sayar, isim ve adres hiç
 * dönmez. Gönderim ayrı bir uç (`dispatchCartRecovery`) ve o da yalnız sayı döner — kişi listesi
 * sunucu tarafında, gönderim anında okunur, HTTP cevabına hiç girmez.
 */
function SepetSegmenti({ yazabilir }: { yazabilir: boolean }) {
  const [olcut, setOlcut] = useState({ countryCode: '', languageCode: '', customerType: '' });
  const [sonuc, setSonuc] = useState<AbandonedCartSegment | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [kanal, setKanal] = useState<string>('EMAIL');
  const [kampanya, setKampanya] = useState('');
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [gonderimSonucu, setGonderimSonucu] = useState<CartRecoveryDispatchResult | null>(null);
  const [gonderimHata, setGonderimHata] = useState<string | null>(null);
  // P1-5: bu kanal için son bilinen teslimat durumu. Yalnız bir önizleme ya da gerçek gönderim
  // cevabından gelir; kanal değişince sıfırlanır, çünkü bir kanalın durumu diğerine taşınamaz.
  const [kanalDurumu, setKanalDurumu] = useState<{ state: 'CONFIGURED' | 'NOT_CONFIGURED'; detail: string } | null>(
    null,
  );
  const metin = PAZARLAMA_METIN.sepet;

  useEffect(() => setKanalDurumu(null), [kanal]);

  const olcutGovdesi = () => ({
    ...(olcut.countryCode === '' ? {} : { countryCode: olcut.countryCode }),
    ...(olcut.languageCode === '' ? {} : { languageCode: olcut.languageCode }),
    ...(olcut.customerType === '' ? {} : { customerType: olcut.customerType }),
  });

  const olc = async () => {
    setHata(null);
    try {
      setSonuc(await api.cartSegment(olcutGovdesi()));
    } catch (caught) {
      setSonuc(null);
      setHata(hataMetni(caught, metin.hata));
    }
  };

  const gonder = async (kuru: boolean) => {
    setGonderimHata(null);
    setGonderimSonucu(null);
    setGonderiliyor(true);
    try {
      const cevap = await api.dispatchCartRecovery({
        channel: kanal,
        ...(kampanya === '' ? {} : { campaignRef: kampanya }),
        ...olcutGovdesi(),
        dryRun: kuru,
      });
      setGonderimSonucu(cevap);
      setKanalDurumu(cevap.delivery);
    } catch (caught) {
      setGonderimHata(hataMetni(caught, metin.hataGonderim));
    } finally {
      setGonderiliyor(false);
    }
  };

  return (
    <section className="content-section" data-testid="cart-segment" aria-labelledby="cart-segment-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="cart-segment-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
      </div>

      <div className="callout" role="note">{metin.gizlilikNotu}</div>

      {hata ? <div className="error callout" role="alert">{hata}</div> : null}

      <h3>{metin.olcutBasligi}</h3>
      <div className="toolbar">
        <label>
          {metin.ulke}{' '}
          <input value={olcut.countryCode} onChange={(olay) => setOlcut({ ...olcut, countryCode: olay.target.value })} maxLength={2} />
        </label>
        <label>
          {metin.dil}{' '}
          <input value={olcut.languageCode} onChange={(olay) => setOlcut({ ...olcut, languageCode: olay.target.value })} />
        </label>
        <label>
          {metin.musteriTuru}{' '}
          <select value={olcut.customerType} onChange={(olay) => setOlcut({ ...olcut, customerType: olay.target.value })}>
            <option value="">{ORTAK.tur}</option>
            {/* Karşılıklar tek tabloda: aynı kelimenin iki ekranda ikiye ayrılmaması için. */}
            {Object.entries(MUSTERI_TURU).map(([kod, ad]) => (
              <option key={kod} value={kod}>{ad}</option>
            ))}
          </select>
        </label>
        <button type="button" data-testid="cart-segment-run" onClick={() => void olc()}>{metin.olc}</button>
      </div>

      {sonuc === null ? null : sonuc.totalQuotes === 0 ? (
        <p className="muted" data-testid="cart-segment-empty">{metin.bosSonuc}</p>
      ) : (
        <div data-testid="cart-segment-result">
          <div className="stat-row">
            <span>{metin.toplamTeklif}: <strong>{tamSayi(sonuc.totalQuotes)}</strong></span>
            <span>{metin.toplamMusteri}: <strong>{tamSayi(sonuc.totalCustomers)}</strong></span>
            <span>{metin.bekleyenPencere}: <strong>{tamSayi(sonuc.waitingThreshold.idleMinutes)} {metin.dakika}</strong></span>
            {sonuc.waiting.avgWaitingDays !== null ? (
              <span>{metin.ortalamaBeklemeGunu}: <strong>{tamSayi(Math.round(sonuc.waiting.avgWaitingDays))} {metin.gun}</strong></span>
            ) : null}
          </div>
          <p className="muted">{sonuc.waitingThreshold.detail}</p>
          {sonuc.discountSource.withManualDiscount > 0 ? <p className="muted">{metin.indirimBilgisi(tamSayi(sonuc.discountSource.withManualDiscount))}</p> : null}

          <div className="table-wrap">
            <table>
              <thead><tr><th>{metin.kirilimUlke}</th><th>{ORTAK.kayit}</th></tr></thead>
              <tbody>{sonuc.breakdown.byCountry.map((satir) => <tr key={satir.countryCode ?? TANIMSIZ}><td>{satir.countryCode ?? TANIMSIZ}</td><td>{tamSayi(satir.count)}</td></tr>)}</tbody>
            </table>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>{metin.kirilimKalem}</th><th>{ORTAK.kayit}</th></tr></thead>
              <tbody>{sonuc.breakdown.byItemType.map((satir) => <tr key={satir.itemType}><td>{satir.itemType}</td><td>{tamSayi(satir.count)}</td></tr>)}</tbody>
            </table>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>{metin.kirilimParaBirimi}</th><th>{ORTAK.kayit}</th><th>{ORTAK.tutar}</th></tr></thead>
              <tbody>{sonuc.breakdown.byCurrency.map((satir) => <tr key={satir.currencyCode}><td>{satir.currencyCode}</td><td>{tamSayi(satir.count)}</td><td>{para(satir.totalMinor, satir.currencyCode)}</td></tr>)}</tbody>
            </table>
          </div>
        </div>
      )}

      {yazabilir ? (
        <>
          <h3>{metin.gonderimBasligi}</h3>
          <p className="muted">{metin.gonderimTarif}</p>
          <div className="toolbar">
            <label>
              {metin.kanal}{' '}
              <select value={kanal} onChange={(olay) => setKanal(olay.target.value)}>
                {Object.entries(KANAL).map(([kod, ad]) => (
                  <option key={kod} value={kod}>{ad}</option>
                ))}
              </select>
            </label>
            <label>
              {metin.kampanya}{' '}
              <input value={kampanya} onChange={(olay) => setKampanya(olay.target.value)} />
            </label>
            <button type="button" data-testid="cart-dispatch-dry-run" disabled={gonderiliyor} onClick={() => void gonder(true)}>{metin.onceOlcOnizle}</button>
            <button
              type="button"
              data-testid="cart-dispatch-send"
              disabled={gonderiliyor || kanalDurumu?.state !== 'CONFIGURED'}
              onClick={() => void gonder(false)}
            >
              {gonderiliyor ? metin.gonderiliyor : metin.gonder}
            </button>
          </div>
          {/* P1-5: yapılandırılmamış ya da henüz doğrulanmamış bir kanalda düğme neden kapalı
              görünür şekilde söylenir; kullanıcı yalnız bir denemeden sonra öğrenmez. */}
          {kanalDurumu === null ? (
            <p className="muted" data-testid="cart-dispatch-unverified">{metin.gonderOnceOnizle}</p>
          ) : kanalDurumu.state === 'NOT_CONFIGURED' ? (
            <p className="muted" data-testid="cart-dispatch-blocked-reason">{kanalDurumu.detail}</p>
          ) : null}

          {gonderimHata ? <div className="error callout" role="alert">{gonderimHata}</div> : null}

          {gonderimSonucu ? (
            <div data-testid="cart-dispatch-result">
              {gonderimSonucu.delivery.state === 'NOT_CONFIGURED' ? (
                <div className="callout" role="alert" data-testid="cart-dispatch-not-configured">
                  <strong>{metin.kanalYapilandirilmamisBaslik}.</strong> {gonderimSonucu.delivery.detail}
                </div>
              ) : (
                <>
                  <p className="callout" role="status">{gonderimSonucu.dryRun ? metin.dryRunSonuc : metin.gonderimTamam}</p>
                  <div className="stat-row">
                    <span>{metin.aday}: <strong>{tamSayi(gonderimSonucu.candidates)}</strong></span>
                    <span>{metin.gonderildi}: <strong>{tamSayi(gonderimSonucu.dispatched ?? 0)}</strong></span>
                    <span>{metin.adressiz}: <strong>{tamSayi(gonderimSonucu.skippedNoAddress ?? 0)}</strong></span>
                    <span>{metin.izinsiz}: <strong>{tamSayi(gonderimSonucu.blockedByConsent ?? 0)}</strong></span>
                    <span>{metin.yapilandirilmamis}: <strong>{tamSayi(gonderimSonucu.notConfigured ?? 0)}</strong></span>
                    <span>{metin.basarisiz}: <strong>{tamSayi(gonderimSonucu.failed ?? 0)}</strong></span>
                  </div>
                </>
              )}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

/** Defterin son fiilleri. */
function RizaDefteri() {
  const [sayfa, setSayfa] = useState<ConsentLedgerPage | null>(null);
  const [kanal, setKanal] = useState('');
  const [durum, setDurum] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const metin = PAZARLAMA_METIN.riza;

  const yukle = useCallback(async () => {
    setHata(null);
    try {
      setSayfa(await api.consentLedger({ ...(kanal === '' ? {} : { channel: kanal }), ...(durum === '' ? {} : { status: durum }) }));
    } catch (caught) {
      setHata(hataMetni(caught, metin.hata));
    }
  }, [kanal, durum, metin.hata]);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  return (
    <section className="content-section" data-testid="consent-ledger" aria-labelledby="consent-ledger-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="consent-ledger-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
        <span className="count-badge">{tamSayi(sayfa?.total ?? 0)} {ORTAK.kayit}</span>
      </div>

      {hata ? <div className="error callout" role="alert">{hata}</div> : null}

      <div className="toolbar">
        <label>
          {metin.sutunKanal}{' '}
          <select value={kanal} onChange={(olay) => setKanal(olay.target.value)}>
            <option value="">{metin.tumKanallar}</option>
            {KANALLAR.map((deger) => <option key={deger} value={deger}>{KANAL[deger]}</option>)}
          </select>
        </label>
        <label>
          {metin.sutunDurum}{' '}
          <select value={durum} onChange={(olay) => setDurum(olay.target.value)}>
            <option value="">{metin.tumDurumlar}</option>
            {DURUMLAR.map((deger) => <option key={deger} value={deger}>{RIZA_DURUMU[deger]}</option>)}
          </select>
        </label>
      </div>

      <div className="table-wrap">
        <table data-testid="consent-ledger-table">
          <thead>
            <tr>
              <th>{metin.sutunAn}</th>
              <th>{metin.sutunKisi}</th>
              <th>{metin.sutunKanal}</th>
              <th>{metin.sutunDurum}</th>
              <th>{metin.sutunKaynak}</th>
              <th>{metin.sutunMetin}</th>
              <th>{metin.sutunKayit}</th>
            </tr>
          </thead>
          <tbody>
            {(sayfa?.items.length ?? 0) === 0 ? (
              <tr><td colSpan={7} className="muted">{metin.bosListe}</td></tr>
            ) : (
              sayfa?.items.map((satir) => (
                <tr key={satir.id}>
                  <td>{tarihSaat(satir.occurredAt)}</td>
                  {/* Kişinin adresi/telefonu: kaydın kendisi, ekranın cümlesi değil. */}
                  <td data-veri>{satir.subjectKey}</td>
                  <td>{karsilik(KANAL, satir.channel)}</td>
                  <td>{karsilik(RIZA_DURUMU, satir.status)}</td>
                  <td data-veri>{satir.source}</td>
                  <td><code>{satir.text?.version ?? satir.textVersionId}</code></td>
                  {/*
                    Rızayı kaydeden aslın adı (`machine:v3-site`, bir kullanıcı kimliği ...).
                    Kanıtın parçası ve çevrilmiyor; ekran metni sayılmıyor (ölçüldü 28.08.2026:
                    "ekranda v3 geçmesin" kuralını tetikleyen sütun buydu).
                  */}
                  <td data-veri>{satir.recordedBy}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Pazarlama denetim izi. */
function DenetimIzi() {
  const [sayfa, setSayfa] = useState<MarketingLogPage | null>(null);
  const [eylem, setEylem] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const metin = PAZARLAMA_METIN.gunluk;

  const yukle = useCallback(async () => {
    setHata(null);
    try {
      setSayfa(await api.marketingLog({ pageSize: 50, ...(eylem === '' ? {} : { action: eylem }) }));
    } catch (caught) {
      setHata(hataMetni(caught, metin.hata));
    }
  }, [eylem, metin.hata]);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  const eylemler = [...new Set((sayfa?.items ?? []).map((satir) => satir.action))].sort();

  return (
    <section className="content-section" data-testid="marketing-log" aria-labelledby="marketing-log-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.baslik}</span>
          <h2 id="marketing-log-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
        <span className="count-badge">{tamSayi(sayfa?.total ?? 0)} {ORTAK.kayit}</span>
      </div>

      {hata ? <div className="error callout" role="alert">{hata}</div> : null}

      <div className="toolbar">
        <label>
          {metin.sutunEylem}{' '}
          <select value={eylem} onChange={(olay) => setEylem(olay.target.value)}>
            <option value="">{metin.tumEylemler}</option>
            {eylemler.map((deger) => <option key={deger} value={deger}>{deger}</option>)}
          </select>
        </label>
      </div>

      <div className="table-wrap">
        <table data-testid="marketing-log-table">
          <thead>
            <tr>
              <th>{metin.sutunAn}</th>
              <th>{metin.sutunEylem}</th>
              <th>{metin.sutunAktor}</th>
              <th>{metin.sutunHedef}</th>
              <th>{metin.sutunSonuc}</th>
            </tr>
          </thead>
          <tbody>
            {(sayfa?.items.length ?? 0) === 0 ? (
              <tr><td colSpan={5} className="muted">{metin.bos}</td></tr>
            ) : (
              sayfa?.items.map((satir) => (
                <tr key={satir.id}>
                  <td>{tarihSaat(satir.occurredAt)}</td>
                  <td><code>{satir.action}</code></td>
                  {/*
                    Aktör kimliği ve hedef kaydın kimliği ham veri: bir kişi kimliği ya da makine
                    aslı adı (`machine:v3`) ekranın cümlesi değil, denetim kaydının kendisi.
                    Ölçüldü 28.08.2026: bu iki sütun "v3 ekranda geçmesin" kuralını tetikliyordu.
                  */}
                  <td data-veri>{satir.actorId ?? TANIMSIZ}</td>
                  <td>{karsilik(WORKFLOW_METIN.varlik, satir.targetType)}{satir.targetId === null ? '' : <> · <span data-veri>{satir.targetId}</span></>}</td>
                  <td>{durumAdi(satir.outcome)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Denetim B10: bülten abone listesi. Liste bu ekranda ilk kez görünüyor; MBDTravel v3 yalnız
 * formu bildiriyor (`marketing/newsletter` uç açıklaması, `marketing.service.ts`).
 */
function BultenAboneleri() {
  const [sayfa, setSayfa] = useState<NewsletterSubscriberPage | null>(null);
  const [durum, setDurum] = useState('');
  const [sayfaNo, setSayfaNo] = useState(1);
  const [hata, setHata] = useState<string | null>(null);
  const metin = PAZARLAMA_METIN.bulten;

  const yukle = useCallback(async () => {
    setHata(null);
    try {
      setSayfa(
        await api.newsletterSubscribers({
          page: sayfaNo,
          ...(durum === '' ? {} : { state: durum }),
        }),
      );
    } catch (caught) {
      setHata(hataMetni(caught, metin.hata));
    }
  }, [durum, sayfaNo, metin.hata]);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  const sonSayfa = Math.max(1, Math.ceil((sayfa?.total ?? 0) / (sayfa?.pageSize ?? 25)));

  return (
    <section className="content-section" data-testid="newsletter-subscribers" aria-labelledby="newsletter-subscribers-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="newsletter-subscribers-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
        <span className="count-badge">{tamSayi(sayfa?.total ?? 0)} {ORTAK.kayit}</span>
      </div>

      {hata ? <div className="error callout" role="alert">{hata}</div> : null}

      {sayfa !== null ? (
        <div className="stat-row" data-testid="newsletter-by-state">
          {BULTEN_DURUMLARI.map((deger) => (
            <span key={deger}>
              {karsilik(metin.durum, deger)}: <strong>{tamSayi(sayfa.byState[deger] ?? 0)}</strong>
            </span>
          ))}
        </div>
      ) : null}

      <div className="toolbar">
        <label>
          {metin.sutunDurum}{' '}
          <select
            value={durum}
            onChange={(olay) => {
              setDurum(olay.target.value);
              setSayfaNo(1);
            }}
          >
            <option value="">{metin.tumDurumlar}</option>
            {BULTEN_DURUMLARI.map((deger) => <option key={deger} value={deger}>{karsilik(metin.durum, deger)}</option>)}
          </select>
        </label>
      </div>

      <div className="table-wrap">
        <table data-testid="newsletter-subscribers-table">
          <thead>
            <tr>
              <th>{metin.sutunEposta}</th>
              <th>{metin.sutunDurum}</th>
              <th>{metin.sutunOnayAni}</th>
              <th>{metin.sutunKaynakSayfa}</th>
              <th>{metin.sutunSonOlay}</th>
            </tr>
          </thead>
          <tbody>
            {(sayfa?.items.length ?? 0) === 0 ? (
              <tr><td colSpan={5} className="muted">{metin.bos}</td></tr>
            ) : (
              sayfa?.items.map((satir) => (
                <tr key={satir.id}>
                  <td>{satir.email}</td>
                  <td>{karsilik(metin.durum, satir.state)}</td>
                  <td>{satir.consentAt === null ? TANIMSIZ : tarihSaat(satir.consentAt)}</td>
                  <td>{satir.sourcePage ?? TANIMSIZ}</td>
                  <td>{tarihSaat(satir.lastEventAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p>
        <button type="button" onClick={() => setSayfaNo((onceki) => Math.max(1, onceki - 1))} disabled={sayfaNo <= 1}>
          {ORTAK.onceki}
        </button>{' '}
        {ORTAK.sayfa} {tamSayi(sayfaNo)}/{tamSayi(sonSayfa)}{' '}
        <button
          type="button"
          data-testid="newsletter-next-page"
          onClick={() => setSayfaNo((onceki) => onceki + 1)}
          disabled={sayfaNo >= sonSayfa}
        >
          {ORTAK.sonraki}
        </button>
      </p>
    </section>
  );
}

/**
 * Denetim B10: mutabakat zamanlaması ve turların dökümü. v3'ün altı saatte bir çağırması
 * gereken mutabakat gerçekten koşuyor mu — burada koşmadığı sessizce kaybolmaz, ekrana çıkar.
 */
function MutabakatDurumu() {
  const [durum, setDurum] = useState<NewsletterCadenceStatus | null>(null);
  const [turlar, setTurlar] = useState<NewsletterReconciliationPage | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const metin = PAZARLAMA_METIN.bulten.mutabakat;

  const yukle = useCallback(async () => {
    setHata(null);
    try {
      const [durumCevap, turCevap] = await Promise.all([
        api.newsletterCadenceStatus(),
        api.newsletterReconciliations({ limit: 20 }),
      ]);
      setDurum(durumCevap);
      setTurlar(turCevap);
    } catch (caught) {
      setHata(hataMetni(caught, metin.hata));
    }
  }, [metin.hata]);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  return (
    <section className="content-section" data-testid="newsletter-cadence" aria-labelledby="newsletter-cadence-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{PAZARLAMA_METIN.bulten.ustBaslik}</span>
          <h2 id="newsletter-cadence-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
      </div>

      {hata ? <div className="error callout" role="alert">{hata}</div> : null}

      {durum === null ? null : (
        <div className="stat-row" data-testid="newsletter-cadence-status">
          <span>
            {metin.sonTur}:{' '}
            <strong>{durum.lastRunAt === null ? metin.hicKosmadi : tarihSaat(durum.lastRunAt)}</strong>
            {durum.hoursSinceLastRun === null
              ? ''
              : ` (${tamSayi(Math.round(durum.hoursSinceLastRun))} ${metin.saatOnce})`}
          </span>
          <span>
            {metin.pencere}: <strong>{tamSayi(durum.cadenceHours)} {metin.saat}</strong>
          </span>
          <span className={durum.overdue ? 'callout' : undefined} role={durum.overdue ? 'alert' : undefined}>
            <strong>{durum.overdue ? metin.geciken : metin.zamaninda}</strong>
          </span>
        </div>
      )}

      <div className="table-wrap">
        <table data-testid="newsletter-reconciliation-table">
          <thead>
            <tr>
              <th>{metin.sutunAn}</th>
              <th>{metin.sutunKimden}</th>
              <th>{metin.sutunIddiaEdilen}</th>
              <th>{metin.sutunEslesen}</th>
              <th>{metin.sutunEksik}</th>
              <th>{metin.sutunFarkli}</th>
              <th>{metin.sutunSonuc}</th>
            </tr>
          </thead>
          <tbody>
            {(turlar?.items.length ?? 0) === 0 ? (
              <tr><td colSpan={7} className="muted">{metin.bos}</td></tr>
            ) : (
              turlar?.items.map((satir) => (
                <tr key={satir.id}>
                  <td>{tarihSaat(satir.ranAt)}</td>
                  <td>{satir.byClient}</td>
                  <td>{tamSayi(satir.claimed)}</td>
                  <td>{tamSayi(satir.matched)}</td>
                  <td>{tamSayi(satir.missing)}</td>
                  <td>{tamSayi(satir.diverged)}</td>
                  <td>{satir.clean ? metin.temiz : metin.sorunlu}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Pazarlama bölümünün rıza ve denetim ekranı.
 *
 * `SectionPage` bunu yalnız Pazarlama sayfasında çiziyor. Her alt bölüm kendi iznine bakıyor;
 * izni olmayan için bölüm hiç çizilmiyor, çünkü çizilse de arkadaki uç 403 dönerdi.
 */
export function MarketingConsentPanel({ me }: { me: Me }) {
  const [metinler, setMetinler] = useState<ConsentText[]>([]);
  const okuyabilir = me.permissions.includes('marketing:consent:read');
  const yazabilir = me.permissions.includes('marketing:consent:manage');
  const izGorebilir = me.permissions.includes('marketing:log:read');
  const kitleOlcebilir = me.permissions.includes('marketing:segment');
  const gonderimYapabilir = me.permissions.includes('marketing:write');
  const bultenOkuyabilir = me.permissions.includes('newsletter:read');

  if (!sekmeGorunur('riza', me.permissions)) return null;

  return (
    <>
      {okuyabilir ? <RizaMetinleri yazabilir={yazabilir} onDegisti={setMetinler} /> : null}
      {okuyabilir ? <OnayFormuOnizleme metinler={metinler} /> : null}
      {okuyabilir ? <KisiGecmisi yazabilir={yazabilir} /> : null}
      {kitleOlcebilir ? <GonderimKapisi /> : null}
      {kitleOlcebilir ? <SepetSegmenti yazabilir={gonderimYapabilir} /> : null}
      {okuyabilir ? <RizaDefteri /> : null}
      {bultenOkuyabilir ? <BultenAboneleri /> : null}
      {bultenOkuyabilir ? <MutabakatDurumu /> : null}
      {izGorebilir ? <DenetimIzi /> : null}
    </>
  );
}
